import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Divider, Alert,
  CircularProgress, Chip, Tooltip
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { FileDownload, Refresh } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

const fmt = (v) => v != null ? parseFloat(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—';
const fmtUSD = (v) => v != null ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—';

function StatCard({ label, value, color = 'text.primary', sub }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" fontWeight={700} color={color} sx={{ mt: 0.5, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'knocked_off', label: 'Knocked Off' },
];

export default function Reports() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [statusFilter, setStatusFilter] = useState('');
  const [tab, setTab] = useState('overview'); // 'overview' | 'monthly' | 'supplier' | 'detail'

  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [bySupplier, setBySupplier] = useState([]);
  const [detail, setDetail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate)   params.set('end_date', endDate);
      const qs = params.toString();

      const [sumRes, monthRes, supRes, detRes] = await Promise.all([
        api.get(`/reports/summary${qs ? '?' + qs : ''}`),
        api.get(`/reports/monthly?year=${year}`),
        api.get(`/reports/by-supplier${qs ? '?' + qs : ''}`),
        api.get(`/reports/detail?${qs}${statusFilter ? '&status=' + statusFilter : ''}`),
      ]);
      setSummary(sumRes.data.data);
      setMonthly(monthRes.data.data);
      setBySupplier(supRes.data.data);
      setDetail(detRes.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading reports');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, year, statusFilter]);

  useEffect(() => { loadAll(); }, []);

  const exportDetail = () => {
    const rows = detail.map(r => ({
      'BOE Number': r.boe_number,
      'HAWB': r.hawb_number || '',
      'Date': r.boe_date,
      'Port': r.port_of_entry,
      'Supplier': r.supplier,
      'Currency': r.currency,
      'Invoice Value (USD)': r.invoice_value,
      'Assessable Value (INR)': r.assessable_value,
      'BCD (INR)': r.bcd_amount,
      'AIDC (INR)': r.aidc_amount,
      'SW Surcharge (INR)': r.sw_surcharge,
      'IGST (INR)': r.igst_amount,
      'Total Duty (INR)': r.duty_amount,
      'Status': r.status,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BOE Detail');
    XLSX.writeFile(wb, `BOE_Report_${startDate || 'all'}_${endDate || 'all'}.xlsx`);
  };

  const exportMonthly = () => {
    const rows = monthly.map(r => ({
      'Month': r.month_name,
      'BOE Count': r.boe_count,
      'USD Value': r.usd_value,
      'Assessable (INR)': r.assessable_inr,
      'BCD Paid (INR)': r.bcd_paid,
      'AIDC Paid (INR)': r.aidc_paid,
      'SW Paid (INR)': r.sw_paid,
      'IGST Paid (INR)': r.igst_paid,
      'Total Duty (INR)': r.total_duty,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Monthly_${year}`);
    XLSX.writeFile(wb, `BOE_Monthly_${year}.xlsx`);
  };

  const TAB_BUTTONS = [
    { key: 'overview', label: 'Overview' },
    { key: 'monthly',  label: 'Monthly' },
    { key: 'supplier', label: 'By Supplier' },
    { key: 'detail',   label: 'Detail Table' },
  ];

  return (
    <Box>
      <PageHeader
        title="Import Reports"
        subtitle="BCD · USD · IGST · Duty summary and analytics"
        breadcrumbs={[{ label: 'Reports' }]}
        action={
          <Button startIcon={<Refresh />} onClick={loadAll} disabled={loading} variant="outlined">
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField label="From Date" type="date" fullWidth size="small" value={startDate}
                onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="To Date" type="date" fullWidth size="small" value={endDate}
                onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Year (Monthly)" select fullWidth size="small" value={year}
                onChange={e => setYear(e.target.value)}>
                {YEAR_OPTIONS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Status" select fullWidth size="small" value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}>
                {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="contained" fullWidth onClick={loadAll} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : 'Apply'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
        {TAB_BUTTONS.map(t => (
          <Button key={t.key} variant={tab === t.key ? 'contained' : 'outlined'}
            size="small" onClick={() => setTab(t.key)}>
            {t.label}
          </Button>
        ))}
      </Box>

      {/* ── Overview ── */}
      {tab === 'overview' && summary && (
        <>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Total BOEs" value={summary.total_boe || 0} color="primary.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Knocked Off" value={summary.knocked_off_count || 0} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Pending" value={(parseInt(summary.pending_count || 0) + parseInt(summary.partial_count || 0))} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Total USD Paid" value={fmtUSD(summary.total_usd_value)} color="info.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Total BCD (INR)" value={`₹${fmt(summary.total_bcd)}`} color="error.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Total IGST (INR)" value={`₹${fmt(summary.total_igst)}`} color="text.secondary" />
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Duty Breakdown Summary (INR)</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {[
                    { label: 'BCD (Basic Customs Duty)', value: summary.total_bcd, color: '#ef5350' },
                    { label: 'AIDC', value: summary.total_aidc, color: '#ab47bc' },
                    { label: 'SW Surcharge', value: summary.total_sw, color: '#42a5f5' },
                    { label: 'IGST (ITC)', value: summary.total_igst, color: '#78909c' },
                    { label: 'Total Duty Paid', value: summary.total_duty, color: '#1a237e', bold: true },
                  ].map(item => (
                    <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight={item.bold ? 700 : 400}>{item.label}</Typography>
                      <Typography variant="body2" fontWeight={item.bold ? 700 : 600} color={item.color}>
                        ₹{fmt(item.value)}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>BOE Status Distribution</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {[
                    { label: 'Knocked Off', count: summary.knocked_off_count, color: 'success' },
                    { label: 'Partial', count: summary.partial_count, color: 'warning' },
                    { label: 'Pending', count: summary.pending_count, color: 'error' },
                  ].map(s => (
                    <Box key={s.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                      <Chip label={s.label} color={s.color} size="small" />
                      <Typography variant="h6" fontWeight={700}>{s.count || 0}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Assessable Value (INR)</Typography>
                    <Typography variant="body2" fontWeight={700}>₹{fmt(summary.total_assessable_inr)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* ── Monthly ── */}
      {tab === 'monthly' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button size="small" startIcon={<FileDownload />} onClick={exportMonthly} variant="outlined">
              Export Excel
            </Button>
          </Box>
          {monthly.length === 0 ? (
            <Alert severity="info">No data for {year}.</Alert>
          ) : (
            <>
              <Card sx={{ mb: 2.5 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Monthly Duty Paid (INR) — {year}</Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={monthly} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month_name" tick={{ fontSize: 11 }} tickFormatter={v => v.substring(0, 3)} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                      <RTooltip formatter={(val, name) => [`₹${parseFloat(val || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, name]} />
                      <Legend />
                      <Bar dataKey="bcd_paid"  name="BCD"         fill="#ef5350" />
                      <Bar dataKey="aidc_paid" name="AIDC"        fill="#ab47bc" />
                      <Bar dataKey="sw_paid"   name="SW Surcharge" fill="#42a5f5" />
                      <Bar dataKey="igst_paid" name="IGST"        fill="#78909c" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ px: 2.5, py: 1.5 }}>
                    <Typography variant="subtitle2">Monthly Summary — {year}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Month</TableCell>
                          <TableCell align="right">BOEs</TableCell>
                          <TableCell align="right">USD Value</TableCell>
                          <TableCell align="right">BCD (INR)</TableCell>
                          <TableCell align="right">AIDC (INR)</TableCell>
                          <TableCell align="right">SW (INR)</TableCell>
                          <TableCell align="right">IGST (INR)</TableCell>
                          <TableCell align="right">Total Duty (INR)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthly.map(r => (
                          <TableRow key={r.month_num}>
                            <TableCell>{r.month_name}</TableCell>
                            <TableCell align="right">{r.boe_count}</TableCell>
                            <TableCell align="right">{fmtUSD(r.usd_value)}</TableCell>
                            <TableCell align="right">₹{fmt(r.bcd_paid)}</TableCell>
                            <TableCell align="right">₹{fmt(r.aidc_paid)}</TableCell>
                            <TableCell align="right">₹{fmt(r.sw_paid)}</TableCell>
                            <TableCell align="right">₹{fmt(r.igst_paid)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>₹{fmt(r.total_duty)}</TableCell>
                          </TableRow>
                        ))}
                        {/* Totals row */}
                        <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{monthly.reduce((s, r) => s + (r.boe_count || 0), 0)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtUSD(monthly.reduce((s, r) => s + (parseFloat(r.usd_value) || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{fmt(monthly.reduce((s, r) => s + (parseFloat(r.bcd_paid) || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{fmt(monthly.reduce((s, r) => s + (parseFloat(r.aidc_paid) || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{fmt(monthly.reduce((s, r) => s + (parseFloat(r.sw_paid) || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{fmt(monthly.reduce((s, r) => s + (parseFloat(r.igst_paid) || 0), 0))}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>₹{fmt(monthly.reduce((s, r) => s + (parseFloat(r.total_duty) || 0), 0))}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* ── By Supplier ── */}
      {tab === 'supplier' && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 2.5, py: 1.5 }}>
              <Typography variant="subtitle2">Import Value by Supplier</Typography>
            </Box>
            <Divider />
            {bySupplier.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">No data</Typography></Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell align="right">BOEs</TableCell>
                      <TableCell align="right">Total USD</TableCell>
                      <TableCell align="right">Assessable (INR)</TableCell>
                      <TableCell align="right">BCD (INR)</TableCell>
                      <TableCell align="right">AIDC (INR)</TableCell>
                      <TableCell align="right">SW (INR)</TableCell>
                      <TableCell align="right">IGST (INR)</TableCell>
                      <TableCell align="right">Duty (INR)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bySupplier.map((r, idx) => (
                      <TableRow key={r.supplier} sx={{ bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover' }}>
                        <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{r.supplier}</TableCell>
                        <TableCell align="right">{r.boe_count}</TableCell>
                        <TableCell align="right">{fmtUSD(r.total_usd)}</TableCell>
                        <TableCell align="right">₹{fmt(r.total_assessable)}</TableCell>
                        <TableCell align="right">₹{fmt(r.total_bcd)}</TableCell>
                        <TableCell align="right">₹{fmt(r.total_aidc)}</TableCell>
                        <TableCell align="right">₹{fmt(r.total_sw)}</TableCell>
                        <TableCell align="right">₹{fmt(r.total_igst)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>₹{fmt(r.total_duty)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Detail Table ── */}
      {tab === 'detail' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Showing {detail.length} records
            </Typography>
            <Button size="small" startIcon={<FileDownload />} onClick={exportDetail} variant="outlined" disabled={!detail.length}>
              Export Excel
            </Button>
          </Box>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>BOE No.</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Port</TableCell>
                      <TableCell align="right">USD</TableCell>
                      <TableCell align="right">AV (INR)</TableCell>
                      <TableCell align="right">BCD</TableCell>
                      <TableCell align="right">AIDC</TableCell>
                      <TableCell align="right">SW</TableCell>
                      <TableCell align="right">IGST</TableCell>
                      <TableCell align="right">Duty</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.map(r => (
                      <TableRow key={r.id}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          <Tooltip title={r.hawb_number || ''}>
                            <span>{r.boe_number}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.boe_date}</TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.supplier}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{r.port_of_entry}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmtUSD(r.invoice_value)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>₹{fmt(r.assessable_value)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>₹{fmt(r.bcd_amount)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>₹{fmt(r.aidc_amount)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>₹{fmt(r.sw_surcharge)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, color: 'text.secondary' }}>₹{fmt(r.igst_amount)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>₹{fmt(r.duty_amount)}</TableCell>
                        <TableCell>
                          <Chip
                            label={r.status === 'knocked_off' ? 'KO' : r.status === 'partial' ? 'Partial' : 'Pending'}
                            size="small"
                            color={r.status === 'knocked_off' ? 'success' : r.status === 'partial' ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {detail.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>No records found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
